import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import Dashboard from '../Dashboard';
import { DASHBOARD_PATH } from '../../models/dashboard';

interface FarmDashboardLocationState {
  farmName?: string;
  farmNumber?: string;
}

/**
 * `/dashboard/:farmId` - the same dashboard, sourced from a farm other than the signed-in one.
 *
 * This exists so the admin farms overview can hand off to the farm page it already has rather than growing
 * a parallel read-only copy of it. It is only the wiring: the farm comes from the route, the name and number
 * ride along in the navigation state so the page can title itself without a second request, and everything
 * below is the ordinary dashboard.
 *
 * A deep link pasted without that state still works - the banner falls back to showing just the number, or
 * a placeholder name.
 */
const FarmDashboard: React.FC = () => {
  const { farmId } = useParams<{ farmId: string }>();
  const { state } = useLocation();

  // Without an id there is no second source to render, and falling through to the own-farm dashboard under
  // a /dashboard/:farmId URL would be quietly wrong.
  if (!farmId) {
    return <Navigate to={DASHBOARD_PATH} replace />;
  }

  const { farmName, farmNumber } = (state ?? {}) as FarmDashboardLocationState;

  // Back returns to the queue, which is what an admin sees at /dashboard and where they got here from.
  return <Dashboard source={{ farmId, farmName, farmNumber }} backPath={DASHBOARD_PATH} />;
};

export default FarmDashboard;
